import * as React from 'react'

import styled from '@emotion/styled'
import { Link, Hidden, Button, ExpansionPanelSummary, ExpansionPanelDetails, ExpansionPanel } from '@material-ui/core'
import css from '@emotion/css'
import { heights, colors, breakpoints, fonts } from '../../../styles/variables'
import InsureTennesseePhoto from './InsureTennesseePhoto'
import IssueDialog from './IssueDialog'
import { issueIds } from '../../../helpers/issueIds'
import { getEmSize } from '../../../styles/mixins'
import CriminalJusticeCard from './CriminalJusticeCard'
import EnvironmentCard from './EnvironmentCard'
import HealthcareCard from './HealthcareCard'
import ImmigrationCard from './ImmigrationCard'
import WorkersCard from './WorkersCard'
import StudentsCard from './StudentsCard'
import HealthcarePhoto from './HealthcarePhoto'
import EducationPhoto from './EducationPhoto'
import EnvironmentPhoto from './EnvironmentPhoto'
import WorkersPhoto from './WorkersPhoto'
import PrisonsPhoto from './PrisonsPhoto'

export const IssueHeader = styled.h2`
  font-size: 50px;
  font-weight: 900;
  color: ${colors.darkBlue};
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    font-size: 30px;
  }
`
export const IssueBody = styled.p``

export const Container = styled.div`
  padding-top: 80px;
  padding-bottom: 50px;
  .MuiExpansionPanelSummary-root {
    padding: 0;
    background-color: ${colors.oatmeal};
  }
  .MuiExpansionPanelSummary-content {
    margin: 0;
  }
  .MuiExpansionPanelDetails-root {
    padding: 0;
  }
  .MuiPaper-root {
    box-shadow: none;
  }
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    padding-top: 20px;
  }
`

export const LearnMoreLink = styled(Link)`
  color: ${colors.darkBlue};
  border-bottom: 2px solid ${colors.darkBlue};
  font-weight: 900;
  letter-spacing: 0.1em;
  font-size: 20px;
  &:focus,
  &:hover {
    text-decoration: none;
    cursor: pointer;
  }
`

export const IssuePreviewContainer = styled.div`
  padding-left: 120px;
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    padding-left: 0;
    margin-bottom: 40px;
  }
`

export const IssueContent = styled.div`
  width: 50%;
  padding-right: 50px;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    width: 100%;
    padding-right: 20px;
    padding-left: 20px;
  }
`

export const IssueTitle = styled.h2`
  font-family: ${fonts.title};
  font-size: 80px;
  color: ${colors.lightBlue};
  line-height: 90px;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    font-size: 30px;
    line-height: 30px;
  }
`

export const IssueContainer = styled.div`
  background-color: ${colors.darkBlue};
  padding-left: 80px;
  padding-right: 60px;
  padding-bottom: 50px;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    padding-left: 10px;
    padding-right: 10px;
  }
`

export const InnerIssueContainer = styled.div`
  display: flex;
  padding-top: 40px;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    flex-direction: column;
  }
`

export const IssueImage = styled.div`
  width: 40%;
`

export const MainContent = styled.div`
  width: 60%;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    width: 100%;
    padding-left: 15px;
    padding-right: 15px;
  }
`

export const Sidebar = styled.div`
  width: 40%;
  padding-left: 20px;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    width: 100%;
  }
`

export const SidebarHeader = styled.p`
  color: ${colors.red};
  font-weight: 900;
  font-family: ${fonts.sansSerif};
  letter-spacing: 0.1em;
  margin-bottom: 40px;
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    margin-bottom: 10px;
  }
`

export const SidebarP = styled.p`
  color: ${colors.white};
`

export const SidebarSubheader = styled.a`
  color: ${colors.lightBlue};
  display: block;
  font-family: ${fonts.sansSerif};
  font-size: 20px;
  font-weight: 600;
  margin-top: 20px;
`

export const CloseButton = styled(Button)`
  @media (max-width: ${getEmSize(breakpoints.lg)}em) {
    display: block;
    margin-left: auto;
    margin-right: auto;
    margin-top: 20px;
  }
`

export default function IssueItems() {
  const [openIssueId, setOpenIssueId] = React.useState<issueIds | undefined>(undefined)
  return (
    <Container>
      <ExpansionPanel
        expanded={openIssueId === 'medicare'}
        onChange={(event, expanded) => setOpenIssueId(expanded ? 'medicare' : undefined)}
      >
        <ExpansionPanelSummary>
          <IssuePreviewContainer>
            <IssueContent>
              <IssueHeader id="healthcare">Healthcare for All</IssueHeader>
              <IssueBody>
                We have a moral responsibility to guarantee affordable and high-quality healthcare as a human right. No one should be denied
                healthcare because they cannot afford it. Justin supports the only sensible, cost-effective solution: single-payer Medicare
                for All.
              </IssueBody>
              {openIssueId !== 'medicare' && <LearnMoreLink onClick={() => setOpenIssueId('medicare')}>LEARN MORE</LearnMoreLink>}
            </IssueContent>
            <Hidden mdDown>
              <IssueImage>
                <HealthcarePhoto style={{ height: '100%' }} imgStyle={{ height: '100%' }} />
              </IssueImage>
            </Hidden>
          </IssuePreviewContainer>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <HealthcareCard handleClose={() => setOpenIssueId(undefined)} />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel
        expanded={openIssueId === 'environment'}
        onChange={(event, expanded) => setOpenIssueId(expanded ? 'environment' : undefined)}
      >
        <ExpansionPanelSummary>
          <IssuePreviewContainer>
            <IssueContent>
              <IssueHeader id="environmental-justice">Environmental Justice</IssueHeader>
              <IssueBody>
                Climate change presents an existential threat to our lives. The Green New Deal is an opportunity to stop climate change,
                create millions of good paying jobs, and stand up to fossil fuel corporations that profit from environmental destruction.
              </IssueBody>
              {openIssueId !== 'environment' && <LearnMoreLink onClick={() => setOpenIssueId('environment')}>LEARN MORE</LearnMoreLink>}
            </IssueContent>
            <Hidden mdDown>
              <IssueImage>
                <EnvironmentPhoto style={{ height: '100%' }} imgStyle={{ height: '100%' }} />
              </IssueImage>
            </Hidden>
          </IssuePreviewContainer>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <EnvironmentCard handleClose={() => setOpenIssueId(undefined)} />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel
        expanded={openIssueId === 'immigration'}
        onChange={(event, expanded) => setOpenIssueId(expanded ? 'immigration' : undefined)}
      >
        <ExpansionPanelSummary>
          <IssuePreviewContainer>
            <IssueContent>
              <IssueHeader id="immigration">Building a Welcoming America</IssueHeader>
              <IssueBody>
                America is a diverse nation where all people can access opportunities and resources to achieve their dreams. Immigrant and
                refugee families need safety and access to citizenship. Justin will work towards clearing obstacles to citizenship,
                providing a permanent protection for all and join the call to abolish ICE. When we welcome immigrants as part of our
                national fabric, we all benefit from a more diverse district and entrepreneurial economy.
              </IssueBody>
              {openIssueId !== 'immigration' && <LearnMoreLink onClick={() => setOpenIssueId('immigration')}>LEARN MORE</LearnMoreLink>}
            </IssueContent>
            <Hidden mdDown>
              <IssueImage>
                <InsureTennesseePhoto style={{ height: '100%' }} imgStyle={{ height: '100%' }} />
              </IssueImage>
            </Hidden>
          </IssuePreviewContainer>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <ImmigrationCard handleClose={() => setOpenIssueId(undefined)} />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel
        expanded={openIssueId === 'students'}
        onChange={(event, expanded) => setOpenIssueId(expanded ? 'students' : undefined)}
      >
        <ExpansionPanelSummary>
          <IssuePreviewContainer>
            <IssueContent>
              <IssueHeader id="student-loans">Affordable, Equitable Higher Education</IssueHeader>
              <IssueBody>
                Education improves lives, our communities, and our district. Young people are told that higher education is
                necessary—meanwhile, tuition skyrockets and students struggle with $1.5 trillion in debt. Justin supports eliminating
                student debt and funding tuition-free higher education at public universities, vocational schools, and trade schools. He
                supports increasing funding for Historically Black Colleges and Universities (HBCUs).
              </IssueBody>
              {openIssueId !== 'students' && <LearnMoreLink onClick={() => setOpenIssueId('students')}>LEARN MORE</LearnMoreLink>}
            </IssueContent>
            <Hidden mdDown>
              <IssueImage>
                <EducationPhoto style={{ height: '100%' }} imgStyle={{ height: '100%' }} />
              </IssueImage>
            </Hidden>
          </IssuePreviewContainer>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <StudentsCard handleClose={() => setOpenIssueId(undefined)} />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel expanded={openIssueId === 'workers'} onChange={(event, expanded) => setOpenIssueId(expanded ? 'workers' : undefined)}>
        <ExpansionPanelSummary>
          <IssuePreviewContainer>
            <IssueContent>
              <IssueHeader id="workers-rights">An Economy for People</IssueHeader>
              <IssueBody>
                Tennessee is home to thriving industries including construction, healthcare, agriculture, tourism and many others. However,
                thriving business does not equate to thriving wages or safe work conditions. Justin will advocate for Tennessee workers to
                ensure that all are treated fairly, paid living wages, and have equal protection under the law. Justin stands with working
                families to fight for an economy that values people above profits. Justin’s campaign is funded by small dollars and people,
                not corporations.
              </IssueBody>
              {openIssueId !== 'workers' && <LearnMoreLink onClick={() => setOpenIssueId('workers')}>LEARN MORE</LearnMoreLink>}
            </IssueContent>
            <Hidden mdDown>
              <IssueImage>
                <WorkersPhoto style={{ height: '100%' }} imgStyle={{ height: '100%' }} />
              </IssueImage>
            </Hidden>
          </IssuePreviewContainer>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <WorkersCard handleClose={() => setOpenIssueId(undefined)} />
        </ExpansionPanelDetails>
      </ExpansionPanel>

      <ExpansionPanel
        expanded={openIssueId === 'mass-incarceration'}
        onChange={(event, expanded) => setOpenIssueId(expanded ? 'mass-incarceration' : undefined)}
      >
        <ExpansionPanelSummary>
          <IssuePreviewContainer>
            <IssueContent>
              <IssueHeader id="criminal-justice-reform">End Mass Incarceration</IssueHeader>
              <IssueBody>
                America incarcerates over 2 million people in jails and prisons. A new approach to criminal justice must end mass
                incarceration, repair its racist legacy, and eliminate profiting off of people in cages. Justin will fight to end mass
                incarceration, cash bail, and the private prison industry. He will push to eliminate mandatory minimums and decriminalize
                marijuana, and support processes of transformative justice.
              </IssueBody>
              {openIssueId !== 'mass-incarceration' && (
                <LearnMoreLink onClick={() => setOpenIssueId('mass-incarceration')}>LEARN MORE</LearnMoreLink>
              )}
            </IssueContent>
            <Hidden mdDown>
              <IssueImage>
                <PrisonsPhoto style={{ height: '100%' }} imgStyle={{ height: '100%' }} />
              </IssueImage>
            </Hidden>
          </IssuePreviewContainer>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <CriminalJusticeCard handleClose={() => setOpenIssueId(undefined)} />
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </Container>
  )
}
